/** @jsxImportSource @emotion/react */
import React, { useContext, useEffect, useState } from 'react'
import css from '@emotion/css/macro'
import { Input } from '../Input'
import { Control, UseFormSetValue, useForm } from 'react-hook-form'
import { Button } from '../Button'
import { CountryAndProvinceInput } from '../ts-components/CountryAndProvinceInput'
import { CountryContext, CountryProvider } from './countryContext'

type InputFields = {
  address_line1: string
  address_line2: string
  city: string
  postcode: string
  state: string
  street: string
}

type ReturnArgs = InputFields & {
  country: string
}

interface Props {
  onChangeProvince?: (value: string) => void
  responsible?: boolean
  initialState?: ReturnArgs
  isModal?: boolean
  onCancel?: () => void
  onSubmit?: ({ args }: { args: ReturnArgs }) => void
  control?: Control<any, any>
  onChangeCountry?: (value: string) => void
  setFormValue?: UseFormSetValue<any>
  invalidFields?: {
    street: boolean
    city: boolean
    postcode: boolean
    state: boolean
    country: boolean
  }
  requiredFields?: {
    street: boolean
    city: boolean
    postcode: boolean
    state: boolean
    country: boolean
  }
  invalidMessages?: {
    street: string
    city: string
    postcode: string
    state: string
    country: string
  }
  /** Tab index configuration for form fields */
  tabIndexConfig?: {
    street?: number
    city?: number
    postcode?: number
    country?: number
    province?: number
  }
}

interface WrapperProps {
  children: React.ReactNode
  isModal: boolean
  onSubmit: (e: React.FormEvent) => void
}

const Wrapper: React.FC<WrapperProps> = ({ children, isModal, onSubmit }) => {
  if (isModal) {
    return (
      <form
        onSubmit={onSubmit}
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          flex: 1;
        `}
      >
        {children}
      </form>
    )
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        flex: 1;
      `}
    >
      {children}
    </div>
  )
}

const Component = ({
  responsible,
  onCancel,
  onSubmit,
  isModal = true,
  initialState,
  onChangeCountry,
  onChangeProvince,
  control: propsControl,
  invalidFields,
  requiredFields,
  invalidMessages,
  tabIndexConfig,
}: Props) => {
  const [value, setValue] = useState(initialState?.street || '')
  const [country, setCountry] = useState(initialState?.country || 'Select')
  const [province, setProvince] = useState(initialState?.state || '')
  const [fieldErrors, setFieldErrors] = useState<string[]>([])

  const countryList = useContext(CountryContext)

  const {
    control,
    setValue: setValueInternally,
    handleSubmit,
  } = useForm<InputFields>({
    defaultValues: {
      street: initialState?.street || '',
      city: initialState?.city || '',
      postcode: initialState?.postcode || '',
      state: initialState?.state || '',
    },
  })

  const onSubmitPressed = (data: InputFields) => {
    setFieldErrors([])

    if (!country || country === '') {
      setFieldErrors((prev) => [...prev, 'country'])
      return
    }

    const args: ReturnArgs = {
      ...data,
      country,
    }

    onSubmit &&
      onSubmit({
        args,
      })
  }

  const onError = (errors: any) => {
    setFieldErrors(Object.keys(errors))

    if (!country || country === 'Select') {
      setFieldErrors((prev) => [...prev, 'country'])
    }
  }

  const onCancelPressed = () => {
    if (value && !initialState) {
      setValue('')
      setCountry('select')
      setProvince('')
      // setFormValue('address_line1', '')
      // setFormValue('address_line2', '')
      setValueInternally('city', '')
      setValueInternally('postcode', '')
      setValueInternally('state', '')
    } else {
      onCancel && onCancel()
    }
  }

  useEffect(() => {
    const input = document.querySelector('.geoapify-autocomplete-input')
    if (input) {
      input?.setAttribute('tabindex', '1')
    }
  }, [])

  return (
    <Wrapper
      isModal={isModal}
      onSubmit={handleSubmit(onSubmitPressed, onError)}
    >
      <div
        css={css`
          display: flex;
          flex: 1;
          flex-direction: column;

          ${responsible &&
          `
            @media (max-width: 768px) {
              flex-direction: column;
              
              > div {
              width: 100%;
              }
            }
          `}
        `}
      >
        <div css={css``}>
          <Input
            fieldName='street'
            invalid={invalidFields?.street || fieldErrors.includes('street')}
            invalidMessage={
              invalidMessages?.street || 'This field is mandatory'
            }
            control={propsControl || control}
            label={`Street Address ${requiredFields?.street ? '*' : ''}`}
            autoComplete='street-address'
            tabIndex={tabIndexConfig?.street}
          />
        </div>
        <div css={css``}>
          <Input
            fieldName='city'
            control={propsControl || control}
            invalid={invalidFields?.city}
            label={`City ${requiredFields?.city ? '*' : ''}`}
            invalidMessage={invalidMessages?.city || 'This field is mandatory'}
            autoComplete='address-level2'
            tabIndex={tabIndexConfig?.city}
          />
        </div>
        <div css={css``}>
          <Input
            fieldName='postcode'
            control={propsControl || control}
            label={`ZIP/Postal Code ${requiredFields?.postcode ? '*' : ''}`}
            invalid={
              invalidFields?.postcode || fieldErrors.includes('postcode')
            }
            invalidMessage={
              invalidMessages?.postcode || 'This field is mandatory'
            }
            autoComplete='postal-code'
            tabIndex={tabIndexConfig?.postcode}
          />
        </div>
        <CountryAndProvinceInput
          countryValue={country}
          provinceValue={province}
          onCountryChange={(countryVal) => {
            setCountry(countryVal || 'Select')
            onChangeCountry?.(countryVal || '')
          }}
          onProvinceChange={(provinceVal) => {
            setProvince(provinceVal || '')
            setValueInternally('state', provinceVal || '')
            onChangeProvince?.(provinceVal || '')
          }}
          valueMode='id'
          provinceValueMode='id'
          fieldErrors={{
            country: invalidFields?.country || fieldErrors.includes('country'),
            state: invalidFields?.state || fieldErrors.includes('state'),
          }}
          customErrors={{
            country: invalidMessages?.country,
            province: invalidMessages?.state,
          }}
          requiredFields={{
            country: requiredFields?.country,
            province: requiredFields?.state,
          }}
          labels={{
            country: 'Country',
            province: 'State/Province',
          }}
          responsive={responsible}
          customCountryList={countryList}
          countryTabIndex={tabIndexConfig?.country}
          provinceTabIndex={tabIndexConfig?.province}
          allowClear={true}
        />
      </div>
      {isModal && (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
            margin-top: 20px;
          `}
        >
          <Button
            onClick={(e) => {
              e.preventDefault()

              onCancelPressed()
            }}
            label={value && !initialState ? 'Clear' : 'Cancel'}
            variant='secondary'
          />
          <Button
            onClick={handleSubmit(onSubmitPressed, onError)}
            type='submit'
            label='Submit'
          />
        </div>
      )}
    </Wrapper>
  )
}

export const NewUserAddressSelector = ({
  responsible,
  onCancel,
  onSubmit,
  onChangeCountry,
  onChangeProvince,
  isModal = true,
  initialState,
  setFormValue,
  control,
  invalidFields,
  requiredFields,
  invalidMessages,
  tabIndexConfig,
}: Props) => {
  return (
    <CountryProvider>
      <Component
        responsible={responsible}
        onCancel={onCancel}
        onSubmit={onSubmit}
        onChangeCountry={onChangeCountry}
        isModal={isModal}
        initialState={initialState}
        setFormValue={setFormValue}
        control={control}
        invalidFields={invalidFields}
        requiredFields={requiredFields}
        invalidMessages={invalidMessages}
        onChangeProvince={onChangeProvince}
        tabIndexConfig={tabIndexConfig}
      />
    </CountryProvider>
  )
}
